import React, { useState } from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Tooltip, 
  Divider, 
  IconButton,
  Typography,
  Collapse
} from '@mui/material';
import {
  Dashboard,
  ElectricBolt,
  Schedule,
  MenuBook,
  TrendingUp,
  ScienceOutlined,
  Settings,
  CloudQueue,
  ChevronLeft,
  ChevronRight,
  PowerSettingsNew,
  Assessment,
  Storage,
  Security,
  Group,
  AdminPanelSettings,
  Person,
  SwapHoriz,
  Logout,
  AccountCircle,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isMultiTenantMode } from '../config/api';
import { useHealth } from '../contexts/HealthContext';
import HealthStatusChip from './HealthStatusChip';
import HealthStatusChipXs from './HealthStatusChipXs';

// Logo's uit de public/images map - dezelfde als in Login.jsx
const dyconLogo = '/images/logo_dycon.svg';
const slimverbruikLogo = '/images/logo_slimverbruik.nl.svg';

const SIDEBAR_WIDTH_EXPANDED = 280;
const SIDEBAR_WIDTH_COLLAPSED = 72;

const Sidebar = ({ onToggle }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [accountMenuExpanded, setAccountMenuExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, selectedEnvironment, logout, clearEnvironmentSelection } = useAuth();
  const { currentEnvironmentHealth, getCurrentEnvironmentDisplay } = useHealth();

  const isExpanded = !collapsed;
  const sidebarWidth = isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  // Haal environment status op uit health context
  const environmentStatus = getCurrentEnvironmentDisplay();

  // Menu structure - geïnspireerd door Portainer
  const menuSections = [];

  // In single tenant mode, toon altijd de electrolyzer menu items
  // In multi-tenant mode, alleen tonen als er een environment is geselecteerd
  if (!isMultiTenantMode() || selectedEnvironment) {
    menuSections.push(
      {
        title: 'Axioniq',
        items: [
          {
            id: 'dashboard',
            label: 'Dashboard',
            icon: <Dashboard />,
            path: '/',
            description: 'Overzicht en status'
          },
          {
            id: 'control',
            label: 'Bediening',
            icon: <ElectricBolt />,
            path: '/control',
            description: 'Direct bedienen van de electrolyzer'
          },
          {
            id: 'planning',
            label: 'Planning',
            icon: <Schedule />,
            path: '/planning',
            description: 'Automatische planning beheren'
          }
        ]
      },
      {
        title: 'Monitoring & Data',
        items: [
          {
            id: 'logbook',
            label: 'Logboek',
            icon: <MenuBook />,
            path: '/logbook',
            description: 'Historische logs en gebeurtenissen'
          },
          {
            id: 'marketdata',
            label: 'Marktprijzen',
            icon: <TrendingUp />,
            path: '/marketdata',
            description: 'Energieprijzen en marktdata'
          },
          {
            id: 'simulation',
            label: 'Simulatie',
            icon: <ScienceOutlined />,
            path: '/simulation',
            description: 'Test scenario\'s en planning'
          }
        ]
      },
      {
        title: 'Configuratie',
        items: [
          {
            id: 'settings',
            label: 'Instellingen',
            icon: <Settings />,
            path: '/settings',
            description: 'Systeem configuratie'
          }
        ]
      }
    );
  }

  // Multi-tenant specifieke menu items - alleen in multi-tenant mode EN alleen voor admins
  if (isMultiTenantMode()) {
    // Admin-only items (Super admin of Company admin)
    const isAdmin = user?.id === 1 || user?.system_role === 'super_admin' || 
      (user?.company_roles && user.company_roles.some(role => role.role === 'company_admin'));
    
    if (isAdmin) {
      const managementItems = [
        {
          id: 'environments',
          label: 'Omgevingen',
          icon: <CloudQueue />,
          path: '/environments',
          description: 'Electrolyzer omgevingen beheren'
        },
        {
          id: 'users',
          label: 'Gebruikers',
          icon: <AdminPanelSettings />,
          path: '/user-management',
          description: 'Gebruikersbeheer en goedkeuringen'
        }
      ];

      // Super admin specifieke items
      if (user?.system_role === 'super_admin') {
        managementItems.push({
          id: 'system-audit',
          label: 'Systeem Audit',
          icon: <Security />,
          path: '/system-audit',
          description: 'Central backend audit logs en rate limiting'
        });
      }

      menuSections.push({
        title: 'Beheer',
        items: managementItems
      });
    }
  }

  const handleItemClick = (item) => {
    // Gebruik React Router navigate voor juiste navigatie
    navigate(item.path);
  };

  const toggleCollapsed = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    if (onToggle) {
      onToggle(newCollapsed);
    }
  };

  const isActive = (path) => {
    const currentPath = location.pathname;
    if (path === '/') {
      return currentPath === '/' || currentPath === '';
    }
    return currentPath.startsWith(path);
  };

  const renderMenuItem = (item) => {
    const active = isActive(item.path);
    
    const listItem = (
      <ListItem 
        key={item.id} 
        disablePadding
        sx={{
          mb: 0.25,
          '&:hover': {
            '& .MuiListItemButton-root': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            }
          }
        }}
      >
        <ListItemButton
          onClick={() => handleItemClick(item)}
          sx={{
            minHeight: 42,
            px: 2,
            borderRadius: 2,
            mx: 1,
            backgroundColor: active ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
            borderLeft: active ? '4px solid #1976d2' : '4px solid transparent',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            }
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isExpanded ? 2 : 'auto',
              justifyContent: 'center',
              color: active ? 'primary.main' : 'text.secondary'
            }}
          >
            {item.icon}
          </ListItemIcon>
          {isExpanded && (
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: active ? 600 : 400,
                color: active ? 'primary.main' : 'text.primary'
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );

    // Wrap met tooltip als collapsed
    if (collapsed) {
      return (
        <Tooltip 
          key={item.id}
          title={
            <Box>
              <Typography variant="subtitle2">{item.label}</Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.7)">
                {item.description}
              </Typography>
            </Box>
          }
          placement="right"
          arrow
        >
          {listItem}
        </Tooltip>
      );
    }

    return listItem;
  };

  const toggleAccountMenu = () => {
    setAccountMenuExpanded(!accountMenuExpanded);
  };

  const handleAccountSettings = () => {
    // Alleen account settings in multi-tenant mode
    if (isMultiTenantMode()) {
      navigate('/account-settings');
    }
    setAccountMenuExpanded(false);
  };

  const handleSwitchEnvironment = () => {
    // Alleen environment switching in multi-tenant mode
    if (isMultiTenantMode()) {
      clearEnvironmentSelection();
    }
    setAccountMenuExpanded(false);
  };

  const handleLogout = () => {
    // Alleen logout in multi-tenant mode
    if (isMultiTenantMode()) {
      logout();
    }
    setAccountMenuExpanded(false);
  };

  return (
    <Box
      sx={{
        width: sidebarWidth,
        flexShrink: 0,
        transition: 'width 0.3s ease',
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1200,
        overflowX: 'hidden',
        overflowY: 'auto'
      }}

    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          minHeight: 56,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        {isExpanded && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src={dyconLogo}
              alt="Dycon Logo"
              style={{ 
                height: '32px', 
                width: 'auto',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
              }}
            />
          </Box>
        )}
        <IconButton
          onClick={toggleCollapsed}
          size="small"
          sx={{
            ml: isExpanded ? 0 : 'auto',
            mr: isExpanded ? 0 : 'auto'
          }}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>

      {/* Environment Info met HealthStatusChipXs */}
      {isMultiTenantMode() && selectedEnvironment && isExpanded && (
        <Box sx={{ p: 1.5, backgroundColor: 'rgba(25, 118, 210, 0.04)' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Huidige omgeving:
          </Typography>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {selectedEnvironment.name}
          </Typography>
          <HealthStatusChipXs 
            showEnvironmentName={false}
            clickable={true}
          />
        </Box>
      )}

      {/* Menu Sections */}
      <Box sx={{ py: 0.5 }}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={section.title}>
            {/* Section Title */}
            {isExpanded && (
              <Typography
                variant="overline"
                sx={{
                  px: 2,
                  py: 0.5,
                  display: 'block',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px'
                }}
              >
                {section.title}
              </Typography>
            )}

            {/* Menu Items */}
            <List sx={{ py: 0 }}>
              {section.items.map(renderMenuItem)}
            </List>

            {/* Divider between sections */}
            {sectionIndex < menuSections.length - 1 && (
              <Divider sx={{ my: 0.5, mx: 2 }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Footer with Account Menu */}
      <Box
        sx={{
          mt: 'auto',
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'rgba(0,0,0,0.02)'
        }}
      >
        {/* Account Section - alleen in multi-tenant mode */}
        {isMultiTenantMode() && (user?.name || user?.email) && (
          <Box>
            {collapsed ? (
              <Box sx={{ p: 0.75 }}>
                <Tooltip 
                  title={
                    <Box>
                      <Typography variant="subtitle2">{user?.name || user?.email}</Typography>
                      <Typography variant="caption" color="rgba(255,255,255,0.7)">
                        Klik voor accountinstellingen
                      </Typography>
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    onClick={handleAccountSettings}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 'auto',
                        justifyContent: 'center',
                        color: 'text.secondary'
                      }}
                    >
                      <AccountCircle />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              </Box>
            ) : (
              <>
                {/* Account Header Button */}
                <Box sx={{ p: 0.75 }}>
                  <ListItemButton
                    onClick={toggleAccountMenu}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      backgroundColor: accountMenuExpanded ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 2,
                        justifyContent: 'center',
                        color: 'text.secondary'
                      }}
                    >
                      <AccountCircle />
                    </ListItemIcon>
                    <ListItemText
                      primary={user?.name || user?.email}
                      secondary={isMultiTenantMode() && selectedEnvironment ? selectedEnvironment.name : 'Account'}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        noWrap: true
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                        noWrap: true
                      }}
                    />
                    {accountMenuExpanded ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </Box>

                {/* Account Submenu */}
                <Collapse in={accountMenuExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {/* Account Settings */}
                    <ListItemButton
                      sx={{
                        pl: 3.5,
                        pr: 2,
                        py: 0.75,
                        mx: 1,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        }
                      }}
                      onClick={handleAccountSettings}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                        <Settings fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Accountinstellingen"
                        primaryTypographyProps={{
                          fontSize: '0.8rem'
                        }}
                      />
                    </ListItemButton>

                    {/* Environment Switcher - only in multi-tenant mode */}
                    {isMultiTenantMode() && (
                      <ListItemButton
                        sx={{
                          pl: 3.5,
                          pr: 2,
                          py: 0.75,
                          mx: 1,
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          }
                        }}
                        onClick={handleSwitchEnvironment}
                      >
                        <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                          <SwapHoriz fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Omgeving wisselen"
                          primaryTypographyProps={{
                            fontSize: '0.8rem'
                          }}
                        />
                      </ListItemButton>
                    )}

                    {/* Logout */}
                    <ListItemButton
                      sx={{
                        pl: 3.5,
                        pr: 2,
                        py: 0.75,
                        mx: 1,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        }
                      }}
                      onClick={handleLogout}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                        <Logout fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Uitloggen"
                        primaryTypographyProps={{
                          fontSize: '0.8rem'
                        }}
                      />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}
          </Box>
        )}

        {/* Powered by section */}
        {isExpanded && (
          <Box sx={{ p: 1.5, pt: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                powered by
              </Typography>
              <img
                src={slimverbruikLogo}
                alt="Slimverbruik.nl"
                style={{ 
                  height: '16px', 
                  width: 'auto',
                  opacity: 0.7
                }}
              />
            </Box>
          </Box>
        )}



      </Box>
    </Box>
  );
};

export default Sidebar; 